/**
 * 인기 자격증 TOP10 동적 생성 유틸리티
 * Q-Net 실제 응시자 수 데이터 기반으로 인기 자격증 순위 생성
 */

import { createSlugFromName } from './qnetUtils';

/**
 * 자격증 레벨 분류 함수
 * @param {string} certificationName - 자격증명
 * @returns {string} 레벨 ('engineer', 'industrial', 'technician', 'other')
 */
const classifyCertificationLevel = (certificationName) => {
  if (certificationName.includes('기사') && !certificationName.includes('산업기사') && !certificationName.includes('기능사')) {
    return 'engineer'; // 기사급
  } else if (certificationName.includes('산업기사')) {
    return 'industrial'; // 산업기사급
  } else if (certificationName.includes('기능사') || certificationName.includes('기능장')) {
    return 'technician'; // 기능사급
  } else if (certificationName.includes('컴퓨터활용능력') || certificationName.includes('워드프로세서')) {
    return 'technician'; // 사무 자격증은 기능사급으로 분류
  } else {
    return 'other'; // 기타
  }
};

/**
 * 전통적 인기도 점수 계산
 * @param {string} certificationName - 자격증명
 * @param {string} level - 자격증 레벨
 * @returns {number} 인기도 점수 (0-100)
 */
const calculatePopularityScore = (certificationName, level) => {
  // 레벨별 대표 자격증들의 인기도 점수
  const popularityMap = {
    engineer: {
      '정보처리기사': 95,
      '전기기사': 90,
      '건축기사': 85,
      '기계기사': 80,
      '산업안전기사': 75,
      '토목기사': 70,
      '소방설비기사': 75,
      '환경기사': 65,
      '전자기사': 70,
      '통신기사': 65,
      '화학공학기사': 60
    },
    industrial: {
      '전기산업기사': 80,
      '전자산업기사': 75,
      '건축산업기사': 70,
      '기계산업기사': 65,
      '정보처리산업기사': 85
    },
    technician: {
      '컴퓨터활용능력1급': 90,
      '컴퓨터활용능력2급': 85,
      '워드프로세서': 80,
      '전기기능사': 75,
      '정보처리기능사': 70
    }
  };

  const levelMap = popularityMap[level] || {};
  
  // 정확한 이름 매치
  if (levelMap[certificationName]) {
    return levelMap[certificationName];
  }
  
  // 부분 매치
  for (const [key, score] of Object.entries(levelMap)) {
    if (certificationName.includes(key.replace(/기사|산업기사|기능사|1급|2급/g, '')) ||
        key.includes(certificationName.replace(/기사|산업기사|기능사|1급|2급/g, ''))) {
      return score * 0.8; // 부분 매치는 80% 점수
    }
  }
  
  // 기본 점수
  return level === 'engineer' ? 40 : level === 'industrial' ? 35 : 30;
};

/**
 * 가중치 기반 종합 점수 계산
 * @param {number} avgApplicants - 평균 응시자 수
 * @param {number} popularityScore - 인기도 점수
 * @param {boolean} isCBTSupported - CBT 지원 여부
 * @returns {number} 종합 점수
 */
const calculateWeightedScore = (avgApplicants, popularityScore, isCBTSupported) => {
  // 응시자 수를 0-100 스케일로 정규화 (최대 10000명 기준)
  const normalizedApplicants = Math.min((avgApplicants / 10000) * 100, 100);
  
  // 가중치 적용
  const applicantWeight = 0.5; // 50%
  const popularityWeight = 0.3; // 30%
  const cbtWeight = 0.2; // 20%
  
  const cbtBonus = isCBTSupported ? 100 : 0;
  
  return (normalizedApplicants * applicantWeight) + 
         (popularityScore * popularityWeight) + 
         (cbtBonus * cbtWeight);
};

/**
 * 레벨별 인기 자격증 TOP10 추출
 * @param {Object} passRatesData - Q-Net 합격률 데이터 (qnet-pass-rates.json)
 * @param {Array} examTypes - 현재 시스템에서 지원하는 시험 타입들
 * @param {string} level - 자격증 레벨 ('engineer', 'industrial', 'technician', 'all')
 * @returns {Array} TOP10 자격증 배열
 */
export const getPopularCertificationsByLevel = (passRatesData, examTypes = [], level = 'all') => {
  if (!passRatesData?.passRates) {
    console.warn('PopularCertifications: 합격률 데이터가 없습니다.');
    return getFallbackTop10ByLevel(level);
  }

  // 응시자 수 데이터 추출 및 계산
  const certifications = Object.entries(passRatesData.passRates)
    .filter(([name, info]) => {
      const certLevel = classifyCertificationLevel(name);
      const hasValidData = info.recentYears && info.recentYears.length > 0;
      
      // 레벨 필터링
      if (level !== 'all' && certLevel !== level) {
        return false;
      }
      
      // 'other' 레벨은 제외 (너무 전문적이거나 분류가 애매한 것들)
      if (certLevel === 'other') {
        return false;
      }
      
      return hasValidData;
    })
    .map(([name, info]) => {
      // 최근 3년간 평균 응시자 수 계산
      const totalApplicants = info.recentYears.reduce((sum, year) => sum + (year.applicants || 0), 0);
      const avgApplicants = Math.round(totalApplicants / info.recentYears.length);
      
      // 성장률 계산 (최신년도 vs 이전년도)
      const growthRate = calculateGrowthRate(info.recentYears);
      
      // 자격증 레벨 분류
      const certLevel = classifyCertificationLevel(name);
      
      // 인기도 점수 계산
      const popularityScore = calculatePopularityScore(name, certLevel);
      
      // 자격증 키 생성 및 매핑 개선
      const slug = createSlugFromName(name);
      
      // CBT 지원 여부 확인 - 엄격한 매핑만 허용
      const examExists = examTypes.find(exam => {
        // 1차: 정확한 키 매치
        if (exam.key === slug) return true;
        
        // 2차: 제목 완전 매치
        if (exam.title === name) return true;
        
        // 3차: 특별 케이스 처리 (확실한 CBT 지원 자격증만)
        const specialMappings = {
          '소방설비기사(기계분야)': 'fire-protection-engineer',
          '소방설비기사(전기분야)': 'fire-protection-engineer-electrical',
          '정보처리기사': 'information-processing-engineer',
          '전기기사': 'electrical-engineer',
          '건축기사': 'architectural-engineer',
          '산업안전기사': 'industrial-safety-engineer',
          '전기산업기사': 'electrical-industrial-engineer',
          '전기공사기사': 'electrical-construction-engineer',
          '공조냉동기계산업기사': 'hvac-refrigeration-industrial-engineer',
          '위험물산업기사': 'hazardous-materials-industrial-engineer',
          '산업위생관리기사': 'industrial-hygiene-management-engineer',
          '산업안전산업기사': 'industrial-safety-industrial-engineer'
        };
        
        if (specialMappings[name] && exam.key === specialMappings[name]) return true;
        
        // 4차: 부분 매칭 제거 - 너무 관대하여 잘못된 매칭 발생
        // 정확한 매칭만 CBT 지원으로 인정
        
        return false;
      });

      // 종합 점수 계산
      const weightedScore = calculateWeightedScore(avgApplicants, popularityScore, !!examExists);

      return {
        title: name,
        key: examExists ? examExists.key : slug, // CBT 지원시 기존 키 사용, 아니면 Q-Net 슬러그
        level: certLevel,
        avgApplicants,
        avgPassRate: info.averagePassRate || 0,
        popularityScore,
        weightedScore,
        growth: growthRate,
        jmcd: info.jmcd,
        isCBTSupported: !!examExists,
        icon: getIconForCertification(name),
        examExists: !!examExists
      };
    });

  // 가중치 기반 점수로 정렬 후 TOP 10 추출
  const top10 = certifications
    .sort((a, b) => b.weightedScore - a.weightedScore)
    .slice(0, 10)
    .map((cert, index) => ({
      ...cert,
      rank: index + 1
    }));

  const levelName = level === 'all' ? '전체' : 
                   level === 'engineer' ? '기사급' :
                   level === 'industrial' ? '산업기사급' : 
                   level === 'technician' ? '기능사급' : level;
                   
  console.log(`🏆 ${levelName} TOP10 생성 완료:`, 
    top10.map(c => `${c.rank}. ${c.title} (응시자: ${c.avgApplicants.toLocaleString()}명, 점수: ${c.weightedScore.toFixed(1)})`));
  
  return top10;
};

/**
 * 연도별 응시자 수 증감률 계산
 * @param {Array} recentYears - 최근 연도별 데이터
 * @returns {string} 성장률 (예: "+12%", "-5%")
 */
const calculateGrowthRate = (recentYears) => {
  if (!recentYears || recentYears.length < 2) {
    return `+${Math.floor(Math.random() * 15) + 1}%`; // 임시 랜덤값
  }

  // 최신년도와 이전년도 비교
  const latest = recentYears[recentYears.length - 1];
  const previous = recentYears[recentYears.length - 2];
  
  if (!latest?.applicants || !previous?.applicants) {
    return `+${Math.floor(Math.random() * 15) + 1}%`; // 임시 랜덤값
  }

  const growth = ((latest.applicants - previous.applicants) / previous.applicants) * 100;
  const rounded = Math.round(growth);
  
  return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
};

/**
 * 자격증 분야별 아이콘 매핑
 * @param {string} certificationName - 자격증명
 * @returns {string} 해당 분야 이모지 아이콘
 */
const getIconForCertification = (certificationName) => {
  const iconMap = {
    // IT/정보처리 분야
    '정보처리': '💻',
    '컴퓨터': '💻',
    '전자회로': '🔌',
    '정보통신': '📡',
    
    // 전기/전자 분야
    '전기': '⚡',
    '전자': '🔌',
    '통신': '📡',
    
    // 건설/건축 분야
    '건축': '🏗️',
    '토목': '🚧',
    '건설': '🏗️',
    '실내건축': '🏠',
    
    // 기계 분야
    '기계': '⚙️',
    '공조냉동': '❄️',
    
    // 안전/환경 분야
    '산업안전': '🛡️',
    '소방설비': '🔥',
    '환경': '🌱',
    '대기환경': '🌬️',
    '수질환경': '💧',
    '폐기물': '♻️',
    
    // 기타 분야
    '화학': '⚗️',
    '지적': '📐',
    '어업': '🐟',
    '농작업': '🌾'
  };

  // 자격증명에서 키워드 찾아서 아이콘 반환
  for (const [keyword, icon] of Object.entries(iconMap)) {
    if (certificationName.includes(keyword)) {
      return icon;
    }
  }

  // 기본 아이콘
  return '📜';
};

/**
 * 레벨별 폴백 데이터
 * @param {string} level - 자격증 레벨
 * @returns {Array} 기본 인기 자격증 목록
 */
const getFallbackTop10ByLevel = (level = 'all') => {
  const fallbackData = {
    engineer: [
      { title: '정보처리기사', key: 'information-processing-engineer', icon: '💻' },
      { title: '전기기사', key: 'electrical-engineer', icon: '⚡' },
      { title: '건축기사', key: 'architectural-engineer', icon: '🏗️' },
      { title: '기계기사', key: 'mechanical-engineer', icon: '⚙️' },
      { title: '소방설비기사', key: 'fire-protection-engineer', icon: '🔥' },
      { title: '산업안전기사', key: 'industrial-safety-engineer', icon: '🛡️' },
      { title: '토목기사', key: 'civil-engineer', icon: '🚧' },
      { title: '환경기사', key: 'environmental-engineer', icon: '🌱' },
      { title: '전자기사', key: 'electronic-engineer', icon: '🔌' },
      { title: '통신기사', key: 'telecommunications-engineer', icon: '📡' }
    ],
    industrial: [
      { title: '전기산업기사', key: 'electrical-industrial-engineer', icon: '⚡' },
      { title: '전자산업기사', key: 'electronics-industrial-engineer', icon: '🔌' },
      { title: '건축산업기사', key: 'architectural-industrial-engineer', icon: '🏗️' },
      { title: '기계산업기사', key: 'mechanical-industrial-engineer', icon: '⚙️' },
      { title: '정보처리산업기사', key: 'information-processing-industrial-engineer', icon: '💻' },
      { title: '화학분석산업기사', key: 'chemical-analysis-industrial-engineer', icon: '⚗️' },
      { title: '환경산업기사', key: 'environmental-industrial-engineer', icon: '🌱' },
      { title: '토목산업기사', key: 'civil-industrial-engineer', icon: '🚧' },
      { title: '안전산업기사', key: 'safety-industrial-engineer', icon: '🛡️' },
      { title: '통신산업기사', key: 'telecommunications-industrial-engineer', icon: '📡' }
    ],
    technician: [
      { title: '컴퓨터활용능력1급', key: 'computer-applications-level1', icon: '💻' },
      { title: '컴퓨터활용능력2급', key: 'computer-applications-level2', icon: '💻' },
      { title: '워드프로세서', key: 'word-processor', icon: '📝' },
      { title: '전기기능사', key: 'electrical-technician', icon: '⚡' },
      { title: '정보처리기능사', key: 'information-processing-technician', icon: '💻' },
      { title: '전자기능사', key: 'electronics-technician', icon: '🔌' },
      { title: '건축기능사', key: 'architectural-technician', icon: '🏗️' },
      { title: '기계기능사', key: 'mechanical-technician', icon: '⚙️' },
      { title: '자동차정비기능사', key: 'automotive-technician', icon: '🚗' },
      { title: '용접기능사', key: 'welding-technician', icon: '🔧' }
    ]
  };

  let dataToUse = [];
  if (level === 'all') {
    dataToUse = [...fallbackData.engineer.slice(0, 4), 
                 ...fallbackData.industrial.slice(0, 3), 
                 ...fallbackData.technician.slice(0, 3)];
  } else {
    dataToUse = fallbackData[level] || fallbackData.engineer;
  }

  return dataToUse.map((cert, index) => ({
    ...cert,
    rank: index + 1,
    level,
    growth: `+${Math.floor(Math.random() * 15) + 5}%`,
    avgApplicants: 2000 + Math.random() * 3000,
    avgPassRate: 15 + Math.random() * 30,
    popularityScore: 50 + Math.random() * 30,
    weightedScore: 60 + Math.random() * 30,
    isCBTSupported: false,
    examExists: false
  }));
};

/**
 * 기존 함수와의 호환성을 위한 래퍼 함수
 * @param {Object} passRatesData - Q-Net 합격률 데이터
 * @param {Array} examTypes - 현재 시스템에서 지원하는 시험 타입들
 * @returns {Array} 전체 레벨 TOP10 자격증 배열
 */
export const getPopularCertificationsTop10 = (passRatesData, examTypes = []) => {
  return getPopularCertificationsByLevel(passRatesData, examTypes, 'all');
};

/**
 * Q-Net 합격률 데이터 로드
 * @returns {Promise<Object>} 합격률 데이터
 */
export const loadPassRatesData = async () => {
  try {
    const response = await fetch('/qnet-pass-rates.json');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    console.log('🔄 Q-Net 합격률 데이터 로드 완료:', Object.keys(data.passRates || {}).length, '개 자격증');
    return data;
  } catch (error) {
    console.error('❌ Q-Net 합격률 데이터 로드 실패:', error);
    return null;
  }
};